import {
	ApplicationContext,
	ConfigurationsContext,
	PreferencesContext,
} from '../../App';
import {
	PalletizerContext,
	RuntimeContext,
} from '../../context/PalletizerContext';
import { useMachineConfig } from '../General';
import { KeyboardContext, KeyboardTrigger, LayoutContent } from '../Layout';
import { Unlock } from '../Modal';
import { lBlueFilter } from '../operation/OperationPage';
import { RobotControlButton } from '../operation/RobotControl';
import close from '../../images/close.png';
import top_1 from '../../images/conveyor_top_1.png';
import top_2 from '../../images/conveyor_top_2.png';
import left_1 from '../../images/conveyor_l_1.png';
import left_2 from '../../images/conveyor_l_2.png';
import right_1 from '../../images/conveyor_r_1.png';
import right_2 from '../../images/conveyor_r_2.png';
import Button from '../PendantButton';
import { TabOption } from '../Sidebar';
import { dateToString } from '../shared/dates';
import {
	COLOR,
	getChangeWrapperNumber,
	isWebDeployment,
} from '../shared/shared';
import {
	Box_t,
	PalletConfiguration,
	Pallet_t,
	BoxDimensions,
	PalletDimensions,
	MachineConfig,
} from 'blackbox';
import { Application, CALIB_TCP_INDEX, PalletizerStatus } from 'libapi';
import { ReactElement, useContext, useEffect, useState } from 'react';
import CheckBox from '../common/Checkbox';
import { PalletModel } from '../pallet_config/Pallets';
import { ComboBox, ConfirmationModal } from '../Settings';
import { useHistory, Prompt } from 'react-router-dom';
import { MultiPickData } from '../pallet_config/Layouts/utilities/types';
import BoxScene from '../three/BoxScene';
import { BoxRenderProps } from '../three/utils';
import BayForm from './ConveyorConfiguration';

interface ConveyorCalibration_t {
	box_index: number; // reference configuration.
	box_dimensions: BoxDimensions; // {width:, length:, height:}
	indexing_corner: string; // "Left" or "Right"
	position: number[];
	date_modified: string; // iso.
}

const default_ConveyorCalibration_t = function (): ConveyorCalibration_t {
	return {
		box_index: 0,
		box_dimensions: { width: 0.0, length: 0.0, height: 0.0 },
		indexing_corner: 'Left',
		position: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
		date_modified: '1914-06-28',
	};
};

interface PalletCalibration_t {
	box_index: number; // reference_configuration
	box_dimensions: BoxDimensions;
	pallet_index: number;
	pallet_dimensions: PalletDimensions;
	position: number[];
	date_modified: string; // iso
}

const default_PalletCalibration_t = function (): PalletCalibration_t {
	return {
		box_index: 0,
		box_dimensions: { width: 0.0, length: 0.0, height: 0.0 },
		pallet_index: -1,
		pallet_dimensions: { width: 0.0, length: 0.0, height: 0.0 },
		position: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
		date_modified: '1914-06-28',
	};
};

interface SlipSheetCalibration_t {
	position: number[];
	date_modified: string; // iso
}

function isPalletOrConveyor(
	obj: any
): obj is PalletCalibration_t | ConveyorCalibration_t {
	return typeof obj.box_index === 'number';
}

export interface SlipSheetPickCalibration_t extends SlipSheetCalibration_t {
	ss_ids: string[];
}

const default_SlipSheetCalibration_t = function (): SlipSheetCalibration_t {
	return {
		position: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
		date_modified: '1914-06-28',
	};
};

const default_SlipSheetPickCalibration_t =
	function (): SlipSheetPickCalibration_t {
		return {
			position: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
			date_modified: '1914-06-28',
			ss_ids: [],
		};
	};

export interface Calibration_t {
	'Conveyor 1': ConveyorCalibration_t;
	'Conveyor 2': ConveyorCalibration_t;
	'Left Pallet': PalletCalibration_t;
	'Right Pallet': PalletCalibration_t;
	'Slip Sheet Pick Left 1': SlipSheetPickCalibration_t;
	'Slip Sheet Pick Left 2': SlipSheetPickCalibration_t;
	'Slip Sheet Pick Right 1': SlipSheetPickCalibration_t;
	'Slip Sheet Pick Right 2': SlipSheetPickCalibration_t;
	'Slip Sheet Place Left': SlipSheetCalibration_t;
	'Slip Sheet Place Right': SlipSheetCalibration_t;
}

const default_Calibration_t = function (): Calibration_t {
	return {
		'Conveyor 1': default_ConveyorCalibration_t(),
		'Conveyor 2': default_ConveyorCalibration_t(),
		'Left Pallet': default_PalletCalibration_t(),
		'Right Pallet': default_PalletCalibration_t(),
		'Slip Sheet Pick Left 1': default_SlipSheetPickCalibration_t(),
		'Slip Sheet Pick Left 2': default_SlipSheetPickCalibration_t(),
		'Slip Sheet Pick Right 1': default_SlipSheetPickCalibration_t(),
		'Slip Sheet Pick Right 2': default_SlipSheetPickCalibration_t(),
		'Slip Sheet Place Left': default_SlipSheetCalibration_t(),
		'Slip Sheet Place Right': default_SlipSheetCalibration_t(),
	};
};

enum CalibrationType {
	Conveyor = 0,
	Pallet = 1,
	Slipsheet = 2,
	SlipsheetPlace = 3,
}
interface CalibrationListDelegateProps {
	pTitle: string;
	pType: CalibrationType;
	pTargets: string[];
	savedCalibration: any;
	save_calibration: any;
	setSavedCalibration: any;
	hasUnsavedTune: boolean;
	setHasUnsavedTune: any;
	showConfirmLeaveModal: boolean;
	setShowConfirmLeaveModal: any;
}

interface TuneItemProps {
	label: string;
	value: number;
	setter: (pLabel: string, pNewValue: number) => void;
	suffix: string;
}

function TuneItem({ label, value, setter, suffix }: TuneItemProps) {
	const { preferences } = useContext(PreferencesContext) as any;

	const clamp = (v: number) => Math.max(-999, Math.min(999, v));
	const setClamped = (v: number) => setter(label, clamp(v));

	const lInputHandlers = getChangeWrapperNumber((pNewValue: number) => {
		setClamped(pNewValue);
	});

	const numericValue = Number(value);
	const smIncrementedValue = numericValue + 1;
	const smDecrementedValue = numericValue - 1;
	const incrementedValue = numericValue + 5;
	const decrementedValue = numericValue - 5;

	const triggerKeyboard: KeyboardTrigger = useContext(KeyboardContext);

	const lTriggerKeyboard = function () {
		if (triggerKeyboard) {
			triggerKeyboard(
				`Enter new ${label} ${suffix}`,
				String(value),
				lInputHandlers.FromString
			);
		}
	};

	return (
		<div className="flex-col width-23 align-center">
			<div className="flex-row">
				<span className="bold-font padding-top">{label + suffix + ': '}</span>
			</div>
			<div className="flex-row flex-between half-padding-top">
				<div
					style={{
						zIndex: 0,
						width: '66px',
						height: '44px',
						position: 'relative',
						borderTopLeftRadius: 0,
						borderBottomLeftRadius: 0,
					}}
				>
					<button
						onClick={() => {
							setClamped(decrementedValue);
						}}
						style={{
							zIndex: 0,
							width: '64px',
							height: '44px',
							position: 'relative',
							borderTopLeftRadius: 22,
							borderBottomLeftRadius: 22,
							borderTopRightRadius: 22,
							borderBottomRightRadius: 22,
						}}
					>
						<div className="medium-font increment-element top left">-5</div>
					</button>
					<div
						style={{
							zIndex: 1,
							content: '""',
							position: 'absolute',
							top: 0,
							right: -1,
							bottom: 0,
							width: '22px',
							backgroundColor: COLOR.SILVER,
							borderTopLeftRadius: 22,
							borderBottomLeftRadius: 22,
						}}
					></div>
				</div>
				<div
					style={{
						zIndex: 0,
						width: '46px',
						height: '44px',
						position: 'relative',
						borderTopLeftRadius: 22,
						borderBottomLeftRadius: 22,
					}}
				>
					<button
						onClick={() => {
							setClamped(smDecrementedValue);
						}}
						style={{
							zIndex: 0,
							width: '44px',
							height: '44px',
							position: 'relative',
							borderTopLeftRadius: 22,
							borderBottomLeftRadius: 22,
						}}
					>
						<div className="medium-font increment-element top mid-left">-1</div>
					</button>
				</div>
				<div className="half-padding"></div>
				<input
					className="half-padding width-20"
					type="number"
					value={value}
					onChange={lInputHandlers.Event}
					onClick={lTriggerKeyboard}
				/>
				<div className="half-padding"></div>
				<div
					style={{
						zIndex: 0,
						width: '46px',
						height: '44px',
						position: 'relative',
						borderTopRightRadius: 22,
						borderBottomRightRadius: 22,
					}}
				>
					<button
						onClick={() => {
							setClamped(smIncrementedValue);
						}}
						style={{
							zIndex: 0,
							width: '44px',
							height: '44px',
							position: 'relative',
							borderTopRightRadius: 22,
							borderBottomRightRadius: 22,
						}}
					>
						<div className="medium-font increment-element top mid-right">+1</div>
					</button>
				</div>
				<div
					style={{
						zIndex: 0,
						width: '66px',
						height: '44px',
						position: 'relative',
						borderTopRightRadius: 22,
						borderBottomRightRadius: 22,
					}}
				>
					<button
						onClick={() => {
							setClamped(incrementedValue);
						}}
						style={{
							zIndex: 0,
							width: '64px',
							height: '44px',
							position: 'relative',
							borderTopRightRadius: 22,
							borderBottomRightRadius: 22,
							borderTopLeftRadius: 22,
							borderBottomLeftRadius: 22,
						}}
					>
						<div className="medium-font increment-element top right">+5</div>
					</button>
					<div
						style={{
							zIndex: 1,
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							bottom: 0,
							width: '22px',
							backgroundColor: COLOR.SILVER,
							borderTopRightRadius: 22,
							borderBottomRightRadius: 22,
						}}
					></div>
				</div>
			</div>
			{}
		</div>
	);
}

function CalibrationListDelegate({
	pTitle,
	pType,
	pTargets,
	savedCalibration,
	save_calibration,
	setSavedCalibration,
	hasUnsavedTune,
	setHasUnsavedTune,
	showConfirmLeaveModal,
	setShowConfirmLeaveModal,
}: CalibrationListDelegateProps) {
	const runtimeContext = useContext(PalletizerContext) as RuntimeContext;
	const status = runtimeContext.operational_state.status;
	const history = useHistory();

	const [lEditingIdx, lSetEditingIdx] = useState<number>(-1);
	const [lTuningIdx, lSetTuningIdx] = useState<number>(-1);
	const lPositionLabels = ['X', 'Y', 'Z', 'Rz', 'Ry', 'Rz`'];
	const lUnitLabels = [' (mm)', ' (mm)', ' (mm)', '(deg)', '', ''];

	const [slipSheetPresent, setSlipSheetPresent] = useState<boolean>(false);
	const app = useContext(ApplicationContext) as Application;
	const lMachineConfig = useMachineConfig(app).lMachineConfig;

	useEffect(() => {
		let lHas = false;
		lMachineConfig?.Bays.forEach(bay => {
			bay.SlipSheetMags.forEach(m => {
				lHas = m || lHas;
			});
		});
		setSlipSheetPresent(lHas);
	}, [lMachineConfig]);

	const [tempCalibration, setTempCalibration] = useState<any>(null);
	const [nextLocation, setNextLocation] = useState<any>(null);

	const [pallets, setPallets] = useState<Pallet_t[]>([]);
	const [palletIdx, setPalletIdx] = useState<number>(0);
	const [palletDimensions, setPalletDimensions] = useState<PalletDimensions>({
		width: 0,
		length: 0,
		height: 0,
	});
	const [allBoxes, setAllBoxes] = useState<Box_t[]>([]);
	const [boxIdx, setBoxIdx] = useState<number>(-1);
	const [combinedBoxDimensions, setCombinedBoxDimensions] =
		useState<PalletDimensions>({
			width: 0,
			length: 0,
			height: 0,
		});

	const [grippers, setGrippers] = useState<any>({});
	const [selectedGripperName, setSelectedGripperName] = useState<string>('');
	const [gripperTcpType, setGripperTcpType] = useState<string>(`Loading...`);
	const [gripperTcpIndex, setGripperTcpIndex] = useState<number>(1);
	const [gripperNames, setGripperNames] = useState<string[]>([
		'Loading grippers...',
	]);

	const [tuningDifference, setTuningDifference] = useState<
		[number, number, number, number, number, number]
	>([0, 0, 0, 0, 0, 0]);

	const [selectedMultiPick, setSelectedMultiPick] = useState<MultiPickData>({
		multipick: 0,
		alongWidth: false,
	});

	useEffect(
		function () {
			app.api.Pallet.get() // Get pallets and boxes
				.then(palletConfig => {
					const palletData = palletConfig?.config?.pallets || [];
					const boxData = palletConfig?.config?.boxes || [];
					setPallets(palletData);
					setAllBoxes(boxData);
					setBoxIdx(0);
				})
				.catch(console.error);
			app.api.GripperConfig.get() // Get grippers
				.then(gripperConfig => {
					setGrippers(gripperConfig);
					const gripperConfigNames = Object.keys(gripperConfig);
					setGripperNames(gripperConfigNames);
					if (gripperConfigNames.length > 0) {
						const defaultGripper = gripperConfigNames.find(
							gripperName => gripperConfig[gripperName].active
						);
						setSelectedGripperName(defaultGripper || gripperConfigNames[0]);
					}
				})
				.catch(console.error);
		},
		[app]
	);

	useEffect(() => {
		setTempCalibration(JSON.parse(JSON.stringify(savedCalibration))); // reset unsaved calibration to saved calibration
		setTuningDifference([0, 0, 0, 0, 0, 0]); //reset tuning difference to 0
		setHasUnsavedTune(false);
	}, [savedCalibration, lTuningIdx]);

	useEffect(() => {
		if (pType === CalibrationType.Conveyor || pType === CalibrationType.Pallet) {
			setGripperTcpIndex(1);
			setGripperTcpType('1');
		} else if (
			pType === CalibrationType.Slipsheet ||
			pType === CalibrationType.SlipsheetPlace
		) {
			setGripperTcpIndex(0);
		}
	}, [selectedGripperName]);

	useEffect(() => {
		const splitGripperTcpType = gripperTcpType.split('x');
		const tcpIndex = Number(splitGripperTcpType[0]);
		if (!isNaN(tcpIndex)) {
			setGripperTcpIndex(tcpIndex);
		} else {
			setGripperTcpIndex(1);
		}
		const tcpType = splitGripperTcpType[1]; // TODO: modify box dimensions with multiplier for width and length
		if (tcpType === 'L') {
			setSelectedMultiPick({ multipick: tcpIndex - 1, alongWidth: false });
		} else {
			setSelectedMultiPick({ multipick: tcpIndex - 1, alongWidth: true });
		}
	}, [gripperTcpType]);

	useEffect(() => {
		if (boxIdx < 0) return;
		const scalingBoxDimensions = {
			length: allBoxes[boxIdx].dimensions.length,
			width: allBoxes[boxIdx].dimensions.width,
			height: allBoxes[boxIdx].dimensions.height,
		};
		if (selectedMultiPick.multipick > 0) {
			if (selectedMultiPick.alongWidth) {
				scalingBoxDimensions.length =
					allBoxes[boxIdx].dimensions.width * (selectedMultiPick.multipick + 1);
				scalingBoxDimensions.width = allBoxes[boxIdx].dimensions.length;
			} else {
				scalingBoxDimensions.length =
					allBoxes[boxIdx].dimensions.length * (selectedMultiPick.multipick + 1);
				scalingBoxDimensions.width = allBoxes[boxIdx].dimensions.width;
			}
		}
		setCombinedBoxDimensions(scalingBoxDimensions);
	}, [selectedMultiPick, boxIdx, allBoxes]);

	useEffect(() => {
		if (palletIdx < 0) return;
		setPalletDimensions(
			pallets[palletIdx]?.Dimensions || {
				width: 0.0,
				length: 0.0,
				height: 0.0,
			}
		);
	}, [palletIdx, pallets]);

	function updateTempCalibration(pLabel: string, pNewValue: number) {
		const cp = { ...tempCalibration };
		const target = pTargets[lTuningIdx];
		const lLabelIdx = lPositionLabels.findIndex(l => l === pLabel);

		if (lLabelIdx >= 0) {
			cp[target].position[lLabelIdx] =
				savedCalibration[target].position[lLabelIdx] -
				Number(savedCalibration[target].tune[lLabelIdx]) +
				pNewValue;

			cp[target].tune[lLabelIdx] = pNewValue;
			setTempCalibration(cp);
			const newTuningDifference = cp[target].tune.map(
				(newTune: number, idx: number) =>
					newTune - Number(savedCalibration[target].tune[idx])
			);
			setTuningDifference(newTuningDifference);

			const hasUnsaved = newTuningDifference.some((diff: number) => diff !== 0);
			setHasUnsavedTune(hasUnsaved);
		}
		console.error('Failed to tune position! Check your config!');
	}

	function saveTune() {
		setSavedCalibration(JSON.parse(JSON.stringify(tempCalibration)));
		app.api.Generic.set(['CALIBRATION'], tempCalibration).catch(console.error);
		app.restartCore();
		setHasUnsavedTune(false);
		return;
	}

	const handleBlockedNavigation = (nextLoc: any) => {
		// Improve typing here
		if (!hasUnsavedTune) return true;
		setShowConfirmLeaveModal(true);
		setNextLocation(nextLoc);
		return false;
	};

	const closeModal = (confirm: boolean) => {
		setShowConfirmLeaveModal(false);
		if (confirm) {
			setHasUnsavedTune(false); // Reset the changes flag
			lSetTuningIdx(-1);
			history.push(nextLocation.pathname);
		}
	};

	const editSaveColor = (
		targetIdx: number,
		tuningIdx: number,
		hasUnsavedTune: boolean
	) => {
		if (targetIdx === tuningIdx) {
			if (hasUnsavedTune) {
				return COLOR.GREEN;
			} else {
				return COLOR.DARK_GREY;
			}
		} else {
			return COLOR.MEDIUM_BLUE;
		}
	};

	return (
		<div className="full-size flex-col align-center ">
			<div className="flex-col align-center full-width double-padding-left double-padding-right">
				{pTargets.map((pTarget, pTargetIdx) => {
					return (
						<div key={pTargetIdx} className="flex-col full-width">
							<div className="flex-row align-center flex-between width-96 container-border background-silver double-padding">
								<span className="color-blue font-5 bold-font">{pTarget}</span>

								{lPositionLabels.map((pLabel, pIdx) => {
									return (
										pIdx < 4 && (
											<span className="color-blue" key={pIdx}>
												<span className="bold-font">{pLabel + ': '}</span>
												{`${
													(
														savedCalibration?.[pTarget]?.position[pIdx] -
														savedCalibration?.[pTarget]?.tune[pIdx]
													).toFixed(2) || 0
												}`}
												{pIdx < 4 &&
													` (${
														savedCalibration?.[pTarget]?.tune[pIdx].toFixed(0) >= 0 ? '+' : ''
													}${savedCalibration?.[pTarget]?.tune[pIdx].toFixed(0) || 0})`}{' '}
											</span>
										)
									);
								})}
								<Button
									pAction={() => {
										if (hasUnsavedTune) {
											setShowConfirmLeaveModal(true);
										} else {
											if (pTargetIdx === lTuningIdx) {
												lSetTuningIdx(-1);
											} else {
												lSetTuningIdx(pTargetIdx);
											}
										}
									}}
									pNormalColorOverride={COLOR.MEDIUM_BLUE}
									pClass="double-padding-left double-padding-right"
								>
									<span className="">{`${
										pTargetIdx === lTuningIdx ? 'Close' : 'Tune'
									}`}</span>
								</Button>
								<Button
									pAction={() => {
										if (pTargetIdx === lTuningIdx) {
											saveTune();
										} else {
											if (hasUnsavedTune) {
												setShowConfirmLeaveModal(true);
											} else {
												lSetEditingIdx(pTargetIdx);
												lSetTuningIdx(-1);
											}
										}
									}}
									pDisabled={pTargetIdx === lTuningIdx && !hasUnsavedTune}
									pNormalColorOverride={editSaveColor(
										pTargetIdx,
										lTuningIdx,
										hasUnsavedTune
									)}
									pClass="double-padding-left double-padding-right"
								>
									<span className="">{`${
										pTargetIdx === lTuningIdx ? 'Save' : 'Edit'
									}`}</span>
								</Button>
							</div>
							{lTuningIdx === pTargetIdx && (
								<div className="flex-col align-center container-border width-96 background-silver double-padding-left double-padding-right">
									<div className="flex-row align-center flex-between padding vh-8 double-padding">
										{lPositionLabels.map((pLabel, pIdx) => {
											return (
												pIdx < 4 && (
													<TuneItem
														key={pIdx}
														value={tempCalibration?.[pTarget]?.tune[pIdx]?.toFixed(0) || 0}
														label={pLabel}
														suffix={lUnitLabels[pIdx]}
														setter={updateTempCalibration}
													/>
												)
											);
										})}
									</div>
									<div className="flex-row align-center flex-between padding width-96 double-padding-top double-padding">
										<div className="flex-col width-15">
											<span className="color-blue bold-font">Select Gripper:</span>
											<ComboBox
												values={gripperNames}
												currentValue={selectedGripperName}
												onChange={pNewName => {
													setSelectedGripperName(String(pNewName));
												}}
											/>
										</div>
										{pType === CalibrationType.Conveyor && (
											<div className="flex-col width-15">
												<span className="color-blue bold-font">Select TCP:</span>
												<select
													className="StyledSelect"
													value={gripperTcpType}
													onChange={e => {
														let v = e.target.value;
														setGripperTcpType(String(v));
													}}
												>
													{grippers[selectedGripperName]?.tcps.map(
														(_: any, pIdx: number) => {
															if (pIdx === 0) {
																return null;
															}
															if (pIdx === 1) {
																return (
																	<option value={pIdx} key={pIdx}>
																		{`Pick ${pIdx}`}
																	</option>
																);
															}
															return [
																<option value={`${pIdx}xL`} key={`${pIdx}xL`}>
																	{pIdx === 0 ? 'Slip sheet' : `Pick ${pIdx}xL`}
																</option>,
																<option value={`${pIdx}xW`} key={`${pIdx}xW`}>
																	{pIdx === 0 ? 'Slip sheet' : `Pick ${pIdx}xW`}
																</option>,
															];
														}
													)}
												</select>
											</div>
										)}
										{(pType === CalibrationType.Pallet ||
											pType === CalibrationType.SlipsheetPlace) && (
											<div className="flex-col width-15">
												<span className="color-blue bold-font">Select Pallet:</span>
												<select
													className="StyledSelect"
													value={palletIdx}
													onChange={e => {
														const lV = Number(e.target?.value);
														setPalletIdx(lV);
														setPalletDimensions(
															pallets[lV]?.Dimensions || {
																length: 0.0,
																width: 0.0,
																height: 0.0,
															}
														);
													}}
												>
													<option value={-1}> On Floor (zero height)</option>
													{pallets.map((p, index) => {
														return (
															<option key={index} value={index}>
																{p.name}
															</option>
														);
													})}
												</select>
											</div>
										)}
										{(pType === CalibrationType.Conveyor ||
											pType === CalibrationType.Pallet) && (
											<div className="flex-col width-15">
												<span className="color-blue bold-font">Select Box:</span>
												<select
													className="StyledSelect"
													value={boxIdx}
													onChange={e => {
														const lV = Number(e.target?.value);
														setBoxIdx(lV);
													}}
												>
													{allBoxes.map((b, index) => {
														return (
															<option key={index} value={index}>
																{b.name}
															</option>
														);
													})}
												</select>
											</div>
										)}
										<Button
											pAction={() => {
												app.selectGripper(selectedGripperName, gripperTcpIndex);
												app.test_position(
													CALIB_TCP_INDEX.Box,
													palletDimensions,
													combinedBoxDimensions,
													pTarget,
													tuningDifference
												);
											}}
											pDisabled={status !== PalletizerStatus.Ready}
											pClass="double-padding-left double-padding-right"
											pNormalColorOverride={
												status === PalletizerStatus.Ready
													? COLOR.LIGHT_BLUE
													: COLOR.DARK_GREY
											}
										>
											<span>Test Position</span>
										</Button>
										<Button
											pAction={() => {
												app.home();
											}}
											pDisabled={status !== PalletizerStatus.Ready}
											pClass="double-padding-left double-padding-right"
										>
											<span className={`font-6 full-width ${'icon-edit-robot'}`} />
										</Button>
									</div>
								</div>
							)}
							<div className="padding"></div>
						</div>
					);
				})}
			</div>
			{lEditingIdx > -1 && (
				<LayoutContent
					pTab={{ Name: '', IconClass: '' }}
					dialog={true}
					key={`${slipSheetPresent}`}
				>
					<>
						{pType === CalibrationType.Conveyor && (
							<ConveyorWizard
								pExit={() => {
									app.restartCore();
									lSetEditingIdx(-1);
								}}
								pTarget={pTargets[lEditingIdx]}
								pConveyorCalibration={savedCalibration[pTargets[lEditingIdx]]}
								pSaveCalibration={save_calibration(pTargets[lEditingIdx])}
								pSlipSheetPresent={slipSheetPresent}
							/>
						)}
						{pType === CalibrationType.Pallet && (
							<PalletWizard
								pExit={() => {
									app.restartCore();
									lSetEditingIdx(-1);
								}}
								pTarget={pTargets[lEditingIdx]}
								pPalletCalibration={savedCalibration[pTargets[lEditingIdx]]}
								pSaveCalibration={save_calibration(pTargets[lEditingIdx])}
								pSlipSheetPresent={slipSheetPresent}
							/>
						)}
						{(pType === CalibrationType.Slipsheet ||
							pType === CalibrationType.SlipsheetPlace) && (
							<SlipSheetWizard
								pExit={() => {
									app.restartCore();
									lSetEditingIdx(-1);
								}}
								pTarget={pTargets[lEditingIdx]}
								pSlipSheetCalibration={savedCalibration[pTargets[lEditingIdx]]}
								pSaveCalibration={save_calibration(pTargets[lEditingIdx])}
							/>
						)}
					</>
				</LayoutContent>
			)}
			<Prompt when={hasUnsavedTune} message={handleBlockedNavigation} />
			{showConfirmLeaveModal && (
				<ConfirmationModal
					t1={'You have unsaved changes.'}
					t2={'Are you sure you want to exit?'}
					bCancel={'Keep Editing'}
					bConfirm={'Discard Changes'}
					onConfirm={() => closeModal(true)}
					onCancel={() => closeModal(false)}
				/>
			)}
		</div>
	);
}

interface SelectGripperStepProps {
	pGripperName: string;
	pSetGripperName: (pNewName: string) => void;
	pGripperTcp: number;
	pSetGripperTcp: (pNewValue: number) => void;
	pSlipSheetPresent: boolean;
	pCalibration:
		| ConveyorCalibration_t
		| PalletCalibration_t
		| SlipSheetCalibration_t;
}

function SelectGripperStep({
	pGripperName,
	pSetGripperName,
	pGripperTcp,
	pSetGripperTcp,
	pSlipSheetPresent,
	pCalibration,
}: SelectGripperStepProps) {
	const app = useContext(ApplicationContext) as Application;

	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const { selected_gripper } = lRuntimeContext;

	const pStatus = lRuntimeContext.operational_state.status;
	const lReady = true; //pStatus === PalletizerStatus.Ready;

	const [gripperNames, setGripperNames] = useState<string[]>([
		'Loading grippers...',
	]);
	const [grippers, setGrippers] = useState<any>({});

	useEffect(() => {
		if (isPalletOrConveyor(pCalibration)) {
			pSetGripperTcp(1);
		} else {
			pSetGripperTcp(0);
		}
		// Get grippers
		app.api.GripperConfig.get()
			.then(function (pGripperConfig) {
				setGrippers(pGripperConfig);
				const gs = Object.keys(pGripperConfig);
				setGripperNames(gs);
				if (gs.length > 0) {
					const defaultGripper = gs.find(
						gripperName => pGripperConfig[gripperName].active
					);
					pSetGripperName(defaultGripper || gs[0]);
				}
			})
			.catch(console.error);
	}, []);

	useEffect(() => {
		if (pGripperName) {
			app.selectGripper(pGripperName, pGripperTcp);
		}
	}, [pGripperName]);

	return (
		<div className="flex-col align-center flex-between full-size">
			<h3 className="font-6 bold-font color-blue">Select Gripper</h3>
			<div className="flex-col align-center full-width">
				{!lReady && (
					<div className="full-size flex-center flex-col align-center">
						<h3>Cannot calibrate when robot is not in Ready state.</h3>
						<div className="padding"></div>
					</div>
				)}

				{lReady && grippers && gripperNames && grippers[pGripperName] && (
					<div className="flex-col align-center full-width">
						<span>GRIPPER:</span>
						<div className="half-padding"></div>
						<ComboBox
							values={gripperNames}
							currentValue={pGripperName}
							onChange={pNewName => {
								pSetGripperName(String(pNewName));
							}}
						/>
						<div className="double-padding"></div>
						{/* <span>TCP:</span>
						<div className="half-padding"></div>
						<select
							className="StyledSelect"
							value={pGripperTcp}
							onChange={e => {
								let v = e.target.value;
								pSetGripperTcp(Number(v));
							}}
						>
							{grippers[pGripperName]?.tcps.map((_: any, pIdx: number) => {
								if (pIdx === 0 && !pSlipSheetPresent) {
									return void 0;
								}
								if (pIdx > 1) { // Only allow Slip sheet and Pick 1, remove this condition to allow all TCPs
									return void 0;
								}
								return (
									<option value={pIdx} key={pIdx}>
										{pIdx === 0 ? 'Slip sheet' : `Pick ${pIdx}`}
									</option>
								);
							})}
						</select>

						<div className="double-padding"></div>
						<Button
							pAction={() => { // Selection now automatically applies with useEffect and isPalletOrConveyor interface check
								app.selectGripper(pGripperName, pGripperTcp);
							}}
							pNormalColorOverride={COLOR.LIGHT_BLUE}
							pClass="double-padding width-40"
						>
							<span className="font-5 bold-font">Apply Selection</span>
						</Button> */}
						<div className="double-padding"></div>
						<span className="font-5 color-blue">
							Selected Gripper:
							{selected_gripper.name !== undefined &&
							selected_gripper.name !== null &&
							selected_gripper.tcp !== undefined &&
							selected_gripper.tcp !== null
								? `${selected_gripper.name === '' ? ' None' : selected_gripper.name}` +
								  `,  TCP: ${
										selected_gripper.name === ''
											? 'None'
											: Number(selected_gripper.tcp) === 0
											? 'Slip Sheet'
											: 'Pick ' + selected_gripper.tcp
								  }`
								: ''}
						</span>
					</div>
				)}
			</div>
			<div className="padding"></div>
		</div>
	);
}

enum ConveyorOrientation {
	FromTop = 0,
	FromLeft = 1,
	FromRight = 2,
}

interface SavePositionStepProps {
	pTip: string;
	pCanSaveCalibration: boolean;
	pShowSaveSuccess: boolean;
	pSetShowSaveSuccess: (pShow: boolean) => void;
	pHasSavedCalibration: boolean;
	pHandleSavePosition: (pCalibrationBeforeSave: any) => void;
	pCalibration:
		| ConveyorCalibration_t
		| PalletCalibration_t
		| SlipSheetCalibration_t;
}

function SavePositionStep({
	pTip,
	pCanSaveCalibration,
	pShowSaveSuccess,
	pSetShowSaveSuccess,
	pHasSavedCalibration,
	pHandleSavePosition,
	pCalibration,
}: SavePositionStepProps) {
	const app = useContext(ApplicationContext) as Application;

	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const pStatus = lRuntimeContext.operational_state.status;
	const lReady = pStatus === PalletizerStatus.Ready;
	const isFreedriving = pStatus === PalletizerStatus.Freedrive;

	const toggle_teach_mode = function () {
		if (app) {
			if (isFreedriving) {
				app.end_manual_mode().catch(console.error);
			} else {
				app.start_manual_mode().catch(console.error);
			}
		}
	};

	return (
		<div className="flex-col align-center flex-between full-size">
			<h3 className="font-6 bold-font color-blue">Set Position</h3>
			{!lReady && !isFreedriving && (
				<div className="full-size flex-center flex-col align-center">
					<h3>Cannot calibrate when robot is not in Ready state.</h3>
				</div>
			)}
			{(lReady || isFreedriving) && !pShowSaveSuccess && (
				<div className="flex-col align-center full-width">
					<div className="flex-col align-center full-width">
						<div className="background-silver rounded double-padding">
							<span className="color-dark-grey font-3">Tip: {pTip}</span>
						</div>
						<div className="double-padding"></div>
						<span className="font-3 color-blue padding-bottom">
							1. Home any additional axes before starting
						</span>

						<RobotControlButton
							pIconClass={'icon-edit-robot'}
							pText={'HOME'}
							pDisabled={false}
							pIsLoading={false}
							pAction={() => app.home()}
							pColor={COLOR.MEDIUM_BLUE}
						/>
						<div className="double-padding"></div>
						<span className="font-3 color-blue padding-bottom">
							2. Enable teach mode and move the gripper to the desired location
						</span>

						{/* <div className="padding"></div> */}
						<Button
							pAction={toggle_teach_mode}
							pNormalColorOverride={COLOR.MEDIUM_BLUE}
							pClass="double-padding width-40"
						>
							<span className="font-5 bold-font">
								{' '}
								{isFreedriving ? 'Disable ' : 'Enable '}Freedrive
							</span>
						</Button>
					</div>
					<div className="double-padding"></div>
					<div className="flex-col align-center full-width">
						<span className="font-3 color-blue padding-bottom">
							3. Click 'Save Calibration' when you are happy with the location
						</span>
						{/* <div className="padding"></div> */}
						<Button
							pAction={() => {
								if (isFreedriving) {
									toggle_teach_mode();
								}
								pHandleSavePosition({ ...pCalibration });
							}}
							pNormalColorOverride={
								pCanSaveCalibration ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY
							}
							pClass="double-padding width-40"
							pDisabled={!pCanSaveCalibration}
						>
							<span className="font-5 bold-font"> Save Calibration </span>
						</Button>
						{/* <CalibratePosition /> */}
					</div>
				</div>
			)}
			{lReady && pShowSaveSuccess && (
				<div className="full-size flex-center flex-col align-center">
					<div className="width-60 double-padding flex-center rounded flex-col align-center">
						<h3 className="font-7 color-green bold-font">Position taught</h3>
						<div className="padding" />
						<div className="flex-row width-100 flex-between align-center padding rounded">
							{['X', 'Y', 'Z'].map((pLabel, pIdx) => {
								return (
									<span key={pIdx} className="color-blue font-5">
										<span className="bold-font font-5">{pLabel + ': '} </span>
										{pCalibration.position[pIdx].toFixed(2)}
									</span>
								);
							})}
						</div>
						<div className="padding" />
						<div className="flex-row width-100 flex-between align-center padding rounded">
							{['Pitch', 'Roll', 'Yaw'].map((pLabel, pIdx) => {
								return (
									<span key={pIdx} className="color-blue font-5">
										<span className="bold-font font-5">{pLabel + ': '} </span>
										{pCalibration.position[pIdx + 3]?.toFixed(2)}
									</span>
								);
							})}
						</div>
					</div>
					<div className="padding" />
					<div className="padding" />
					<Button
						pAction={() => {
							pSetShowSaveSuccess(false);
						}}
						pNormalColorOverride={COLOR.MEDIUM_BLUE}
						pClass="double-padding width-40"
					>
						<span className="bold-font font-5">Retry</span>
					</Button>
				</div>
			)}
			<div className="padding"></div>
		</div>
	);
}

interface BoxAlignStepProps {
	save_item: (k: keyof ConveyorCalibration_t) => (a: any) => void;
	pBoxPosition: number;
	pSetBoxPosition: (pBoxPosition: number) => void;
	pConveyorOrientation: ConveyorOrientation;
	pSetConveyorOrientation: (pOrientation: ConveyorOrientation) => void;
}

function BoxAlignStep({
	save_item,
	pBoxPosition,
	pSetBoxPosition,
	pConveyorOrientation,
	pSetConveyorOrientation,
}: BoxAlignStepProps) {
	const lSetBoxPosition = (pPos: number) => {
		pSetBoxPosition(pPos);

		const LSide = pConveyorOrientation === ConveyorOrientation.FromRight ? 1 : 0;
		if (pPos === LSide) {
			save_item('indexing_corner')('Left');
		} else {
			save_item('indexing_corner')('Right');
		}
	};

	const get_conveyor_image = () => {
		switch (pConveyorOrientation) {
			case ConveyorOrientation.FromTop:
				return pBoxPosition === 0 ? top_1 : top_2;
			case ConveyorOrientation.FromLeft:
				return pBoxPosition === 0 ? left_1 : left_2;
			case ConveyorOrientation.FromRight:
				return pBoxPosition === 0 ? right_1 : right_2;
			default:
				return void 0;
		}
	};

	return (
		<div className="flex-col align-center full-size">
			<h3 className="font-6 bold-font color-blue">
				Select Conveyor Pick Position
			</h3>
			<div className="full-size flex-row flex-around align-center">
				{/* Left filters */}
				<div className="flex-col">
					{/* <div className="flex-col">
						<span className="font-3 bold-font color-blue">Conveyor Orientation</span>
						<div className="half-padding" />
						<select
							value={pConveyorOrientation}
							onChange={e => {
								lSetBoxPosition(0);
								pSetConveyorOrientation(parseInt(e.target?.value, 10));
							}}
						>
							<option value={0}></option>
						</select>
					</div> */}
					{/* <div className="double-padding" /> */}
					<div>
						<span className="font-3 bold-font color-blue">Pick Position</span>
						<div className="half-padding" />
						<div className="flex-row align-center">
							<Button
								pAction={() => {
									lSetBoxPosition(0);
								}}
								pNormalColorOverride={
									pBoxPosition === 0 ? COLOR.MEDIUM_BLUE : COLOR.WHITE
								}
								pClass={`double-padding ${
									pBoxPosition === 0 ? '' : 'blue-inner-border'
								}`}
							>
								<span
									className={`${pBoxPosition === 0 ? 'color-white' : 'color-blue'}`}
								>
									Position 1
								</span>
							</Button>
							<div className="padding" />
							<Button
								pAction={() => {
									lSetBoxPosition(1);
								}}
								pNormalColorOverride={
									pBoxPosition === 1 ? COLOR.MEDIUM_BLUE : COLOR.WHITE
								}
								pClass={`double-padding ${
									pBoxPosition === 1 ? '' : 'blue-inner-border'
								}`}
							>
								<span
									className={`${pBoxPosition === 1 ? 'color-white' : 'color-blue'}`}
								>
									Position 2
								</span>
							</Button>
						</div>
					</div>
				</div>

				{/* visual */}

				<img
					className="width-40 auto-height double-margin-right"
					src={get_conveyor_image()}
					alt="Conveyor top 1"
				/>
			</div>
		</div>
	);
}

interface StepProps {
	save_item: (
		k: keyof ConveyorCalibration_t | keyof PalletCalibration_t
	) => (a: any) => void;
}

interface PalletStepProps extends StepProps {
	pPalletIdx: number;
	pSetPalletIdx: (pIdx: number) => void;
}

function PalletStep({ save_item, pPalletIdx, pSetPalletIdx }: PalletStepProps) {
	const [lPallets, lSetPallets] = useState<Pallet_t[]>([]);
	const [lAllBoxes, lSetAllBoxes] = useState<Box_t[]>([]);

	useEffect(() => {
		save_item('pallet_index')(pPalletIdx);
	}, [pPalletIdx]);

	const app = useContext(ApplicationContext) as Application;
	const { set_all_configs } = useContext(ConfigurationsContext) as any;
	useEffect(
		function () {
			app.api.Pallet.get()
				.then(pRes => {
					set_all_configs(pRes);
					const lPs = pRes?.config?.pallets || [];
					const lBxs = pRes?.config?.boxes || [];
					lSetPallets(lPs);
					lSetAllBoxes(lBxs);
				})
				.catch(console.error);
		},
		[app]
	);

	const { preferences } = useContext(PreferencesContext) as any;

	return (
		<div className="flex-col align-center full-height full-width">
			<h3 className="font-6 bold-font color-blue">
				Choose a pallet for calibration
			</h3>
			<span className="font-3 color-blue half-padding-top">
				Choose any one of your previously configured pallets to calibrate
			</span>
			<div className="flex-row flex-around align-center full-width height-80 double-padding-top">
				{/* <div></div> */}
				<div className="flex-col">
					<span className="font-5 bold-font color-blue">Calibration Pallet</span>
					<div className="half-padding" />
					<select
						value={pPalletIdx}
						onChange={e => {
							const lV = Number(e.target?.value);
							pSetPalletIdx(lV);
						}}
					>
						<option value={-1}> On Floor (zero height)</option>
						{lPallets.map((p, index) => {
							return (
								<option key={index} value={index}>
									{p.name}
								</option>
							);
						})}
					</select>
				</div>

				<div className="width-50 full-height">
					{lPallets.length > 0 && pPalletIdx >= 0 && (
						<PalletModel
							pallet={lPallets[pPalletIdx]}
							allBoxes={lAllBoxes}
							unitsSystem={preferences.units_system}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

interface BoxStepProps extends StepProps {
	pBoxIdx: number;
	pSetBoxIdx: (pBoxIdx: number) => void;
}

function BoxStep({ save_item, pBoxIdx, pSetBoxIdx }: BoxStepProps) {
	const [lBoxes, lSetBoxes] = useState<Box_t[]>([]);

	const app = useContext(ApplicationContext) as Application;
	const { set_all_configs } = useContext(ConfigurationsContext) as any;
	useEffect(
		function () {
			app.api.Pallet.get()
				.then(pRes => {
					set_all_configs(pRes);
					const lBxs = pRes?.config?.boxes || [];
					lSetBoxes(lBxs);
				})
				.catch(console.error);
		},
		[app]
	);

	useEffect(() => {
		// This use effect is also triggered on load, so will set box_index = 0
		save_item('box_index')(pBoxIdx);
	}, [pBoxIdx]);
	const brp: BoxRenderProps = {
		...lBoxes[pBoxIdx]?.dimensions,
		backgroundColor: COLOR.WHITE,
		hasLabels: true,
	};
	return (
		<div className="flex-col align-center full-height full-width">
			<h3 className="font-6 bold-font color-blue">Choose a box for calibration</h3>
			<span className="font-3 color-blue half-padding-top">
				Choose any one of your previously configured boxes to calibrate
			</span>
			{lBoxes.length > 0 && (
				<div className="flex-row flex-around align-center full-width height-80">
					{/* <div></div> */}
					<div className="flex-col">
						<span className="font-5 bold-font color-blue">Calibration Box</span>
						<div className="half-padding" />
						<select
							value={pBoxIdx}
							onChange={e => {
								const lV = Number(e.target?.value);
								pSetBoxIdx(lV);
							}}
						>
							{lBoxes.map((b, index) => {
								return (
									<option key={index} value={index}>
										{b.name}
									</option>
								);
							})}
						</select>
					</div>

					<div className="width-50 full-height">
						<div className="BoxContainer">
							<BoxScene
								{...brp}
								showFaceLabels={true}
								labelPosition={brp.labelSide}
								zoom={0.7}
							/>
						</div>
					</div>
				</div>
			)}
			{lBoxes.length === 0 && (
				<div className="full-height flex-row align-center">
					<h3 className="color-red">Create a box in the configurator first!</h3>
				</div>
			)}
		</div>
	);
}

interface CalibrationWizardProps {
	pExit: () => void;
	pTarget: string;
	pSteps: string[];
	pActiveStep: number;
	pSetActiveStep: (pStep: number) => void;
	pCompletedSteps: boolean[];
	pSetCompletedSteps: (pCompleted: boolean[]) => void;
	pHasSavedCalibration: boolean;
	children: ReactElement;
}

function CalibrationWizard({
	pExit,
	pTarget,
	pSteps,
	pActiveStep,
	pSetActiveStep,
	pCompletedSteps,
	pSetCompletedSteps,
	pHasSavedCalibration,
	children,
}: CalibrationWizardProps) {
	// DIALOG CONTROLS
	const [lAllStepsCompleted, lSetAllStepsCompleted] = useState<boolean>(false);

	const [lPositionIdx, lSetPositionIdx] = useState<number>(0);

	useEffect(() => {
		lSetPositionIdx(pSteps.length - 1);
	}, [pSteps]);

	function handleNext() {
		const lCp = [...pCompletedSteps];
		const lLastStepIdx = pSteps.length - 1;
		if (pActiveStep !== lLastStepIdx || pHasSavedCalibration) {
			lCp[pActiveStep] = true;
		}
		pSetCompletedSteps(lCp);

		if ([...lCp].filter(s => !s).length === 0) {
			lSetAllStepsCompleted(true);
		}

		if (pActiveStep < lLastStepIdx) {
			pSetActiveStep(pActiveStep + 1);
		}
	}

	function handleStepClick(pClickedIndex: number) {
		// If it's the current step
		if (pClickedIndex === pActiveStep) {
			// complete it
			const lCp = [...pCompletedSteps];
			lCp[pClickedIndex] = true;
			pSetCompletedSteps(lCp);

			if ([...lCp].filter(s => !s).length === 0) {
				lSetAllStepsCompleted(true);
			}
		} else {
			// Move to the clicked step
			pSetActiveStep(pClickedIndex);
		}
	}

	return (
		<div className="full-size flex-col">
			<div className="background-silver full-width rounded-top padding-bottom flex-row flex-between align-center double-margin-bottom height-9">
				<h1 className="font-6 bold-font color-blue double-padding-left double-padding-top">
					{`Edit ${pTarget} Calibration`}
				</h1>
				<div className="flex-row align-center">
					<div className="double-margin-top double-margin-right"></div>

					<Button
						pAction={pExit}
						pNormalColorOverride={COLOR.TRANSPARENT}
						className="double-margin-top height-60 double-margin-left double-margin-right"
					>
						<img
							className="full-height auto-width"
							style={{ filter: lBlueFilter }}
							src={close}
							alt="close"
						/>
					</Button>
				</div>
			</div>
			<section className="flex-row flex-between width-100 height-83">
				{/* Left Pane */}
				<div className="double-margin-left flex-col full-height align-center width-23 container-border padding">
					<div className="flex-col full-height full-width">
						{pSteps.map(function (pStep: string, pIdx: number) {
							return (
								<CheckBox
									key={pIdx}
									pCompleted={pCompletedSteps[pIdx]}
									pActive={pIdx === pActiveStep}
									pText={pStep}
									pIndex={pIdx}
									pHandleClick={(pIndex: number) => {
										handleStepClick(pIndex);
									}}
								/>
							);
						})}
					</div>
				</div>
				{/* Right pane */}
				<div className="flex-col flex-between align-center width-70 full-height container-border double-margin-right padding">
					{children}
					{/* Buttons */}
					<div className="flex-row full-width flex-between">
						<Button
							pAction={() => {
								if (pActiveStep > 0) {
									pSetActiveStep(pActiveStep - 1);
								}
							}}
							pNormalColorOverride={COLOR.TRANSPARENT}
							className={`width-20 ${'blue-border'}`}
							pDisabled={pActiveStep === 0}
						>
							<span className={`larger-font color-blue ${'color-blue'}`}>Back</span>
						</Button>
						<Button
							pAction={() => {
								if (pActiveStep === lPositionIdx && pHasSavedCalibration) {
									pExit();
								} else handleNext();
							}}
							pNormalColorOverride={
								pActiveStep === lPositionIdx && !pHasSavedCalibration
									? COLOR.DARK_GREY
									: pHasSavedCalibration
									? COLOR.GREEN
									: COLOR.MEDIUM_BLUE
							}
							className="width-20"
							pDisabled={pActiveStep === lPositionIdx && !pHasSavedCalibration}
						>
							<span className="larger-font">
								{pActiveStep === lPositionIdx ? 'Done' : 'Next'}
							</span>
						</Button>
					</div>
				</div>
			</section>
		</div>
	);
}

interface SlipSheetWizardProps {
	pExit: () => void;
	pTarget: string;
	pSlipSheetCalibration: SlipSheetCalibration_t;
	pSaveCalibration: (
		c: SlipSheetCalibration_t,
		submit: boolean,
		target: keyof Calibration_t
	) => void;
}

function SlipSheetWizard({
	pExit,
	pTarget,
	pSlipSheetCalibration,
	pSaveCalibration,
}: SlipSheetWizardProps) {
	// WIZARD MODEL:
	const lSelectGripperIdx = 0;
	const lSetPositionIdx = 1;
	const lSteps = ['Select Gripper', 'Set Position'];

	const [lActiveStep, lSetActiveStep] = useState<number>(0);
	const [lCompletedSteps, lSetCompletedSteps] = useState<boolean[]>([false]);

	// SAVING STUFF: REFACTOR LATER. DONT LIKE ALL TYPES HERE
	const save_item =
		(
			k:
				| keyof PalletCalibration_t
				| keyof ConveyorCalibration_t
				| SlipSheetCalibration_t
		) =>
		(a: any) => {
			k = k as keyof SlipSheetCalibration_t;
			const cp = { ...pSlipSheetCalibration };
			(cp as any)[k] = a;
			// save if position === "k"
			pSaveCalibration(cp, 'position' === k, pTarget as keyof Calibration_t);
		};

	// SAVE POSITION STUFF
	const app = useContext(ApplicationContext) as Application;

	const [lHasSavedCalibration, lSetHasSavedCalibration] =
		useState<boolean>(false);
	const [lShowSaveSuccess, lSetShowSaveSuccess] = useState<boolean>(false);

	const can_save_calibration = () => {
		return (
			(lCompletedSteps.length === 1 && lCompletedSteps[0]) ||
			lCompletedSteps.slice(0, lSetPositionIdx).filter(c => !c).length === 0
		);
	};

	const [lCalibrationBeforeSave, lSetCalibrationBeforeSave] =
		useState<SlipSheetCalibration_t | null>(null);
	useEffect(() => {
		if (!lCalibrationBeforeSave) return;
		if (
			JSON.stringify(lCalibrationBeforeSave) !==
			JSON.stringify(pSlipSheetCalibration)
		) {
			const lCp = [...lCompletedSteps];
			lCp[lSetPositionIdx] = true;
			lSetCompletedSteps(lCp);
			lSetShowSaveSuccess(true);
			lSetHasSavedCalibration(true);
		}
	}, [pSlipSheetCalibration]);

	function lHandleSavePosition(pCalibrationBeforeSave: SlipSheetCalibration_t) {
		lSetCalibrationBeforeSave(pCalibrationBeforeSave);
		if (app) {
			app
				.get_position(CALIB_TCP_INDEX.SlipSheet)
				.then(p => {
					if (!('posx' in (p as any))) {
						console.error('get_position', p);
					} else {
						save_item('position')((p as any)['posx']);
					}
				})
				.catch(console.error);
		}
	}

	// Need to read gripper value from mqtt.
	const [lGripperName, lSetGripperName] = useState<string>('');
	const [lGripperTcp, lSetGripperTcp] = useState<number>(1);

	return (
		<CalibrationWizard
			pExit={pExit}
			pTarget={pTarget}
			pSteps={lSteps}
			pActiveStep={lActiveStep}
			pSetActiveStep={lSetActiveStep}
			pCompletedSteps={lCompletedSteps}
			pSetCompletedSteps={lSetCompletedSteps}
			pHasSavedCalibration={lHasSavedCalibration}
		>
			{
				<section className="full-size padding-left padding-right">
					{lActiveStep === lSelectGripperIdx && (
						<SelectGripperStep
							pGripperName={lGripperName}
							pSetGripperName={lSetGripperName}
							pGripperTcp={lGripperTcp}
							pSetGripperTcp={lSetGripperTcp}
							pSlipSheetPresent={true}
							pCalibration={pSlipSheetCalibration}
						/>
					)}
					{lActiveStep === lSetPositionIdx && (
						<SavePositionStep
							pTip="You can calibrate with any number of slip sheets in the magazine."
							pCanSaveCalibration={can_save_calibration()}
							pShowSaveSuccess={lShowSaveSuccess}
							pSetShowSaveSuccess={lSetShowSaveSuccess}
							pHasSavedCalibration={lHasSavedCalibration}
							pHandleSavePosition={lHandleSavePosition}
							pCalibration={pSlipSheetCalibration}
						/>
					)}
				</section>
			}
		</CalibrationWizard>
	);
}

interface PalletWizardProps {
	pExit: () => void;
	pTarget: string;
	pPalletCalibration: PalletCalibration_t;
	pSaveCalibration: (
		c: PalletCalibration_t,
		submit: boolean,
		target: keyof Calibration_t
	) => void;
	pSlipSheetPresent: boolean;
}

function PalletWizard({
	pExit,
	pTarget,
	pPalletCalibration,
	pSaveCalibration,
	pSlipSheetPresent,
}: PalletWizardProps) {
	// WIZARD MODEL:
	const lChooseBoxIdx = 0;
	const lChoosePalletIdx = 1;
	const lSelectGripperIdx = 2;
	const lSetPositionIdx = 3;
	const lSteps = [
		'Choose Box',
		'Choose Pallet',
		'Select Gripper',
		'Set Position',
	];

	const [lActiveStep, lSetActiveStep] = useState<number>(lChooseBoxIdx);
	const [lCompletedSteps, lSetCompletedSteps] = useState<boolean[]>([
		false,
		false,
		false,
	]);

	// SAVING STUFF: REFACTOR LATER. DONT LIKE ALL TYPES HERE
	const save_item =
		(k: keyof PalletCalibration_t | keyof ConveyorCalibration_t) => (a: any) => {
			k = k as keyof PalletCalibration_t;
			const cp = { ...pPalletCalibration };
			(cp as any)[k] = a;
			// save if position === "k"
			pSaveCalibration(cp, 'position' === k, pTarget as keyof Calibration_t);
		};

	// SAVE POSITION STUFF
	const app = useContext(ApplicationContext) as Application;

	const [lHasSavedCalibration, lSetHasSavedCalibration] =
		useState<boolean>(false);
	const [lShowSaveSuccess, lSetShowSaveSuccess] = useState<boolean>(false);

	const can_save_calibration = () =>
		lCompletedSteps.slice(0, lSetPositionIdx - 1).filter(c => !c).length === 0;

	const [lCalibrationBeforeSave, lSetCalibrationBeforeSave] =
		useState<PalletCalibration_t | null>(null);
	useEffect(() => {
		if (!lCalibrationBeforeSave) return;
		if (
			JSON.stringify(lCalibrationBeforeSave) !== JSON.stringify(pPalletCalibration)
		) {
			const lCp = [...lCompletedSteps];
			lCp[lSetPositionIdx] = true;
			lSetCompletedSteps(lCp);
			lSetShowSaveSuccess(true);
			lSetHasSavedCalibration(true);
		}
	}, [pPalletCalibration]);

	function lHandleSavePosition(pCalibrationBeforeSave: PalletCalibration_t) {
		lSetCalibrationBeforeSave(pCalibrationBeforeSave);
		if (app) {
			app
				.get_position(CALIB_TCP_INDEX.Box)
				.then(p => {
					if (!('posx' in (p as any))) {
						console.error('get_position', p);
					} else {
						save_item('position')((p as any)['posx']);
					}
				})
				.catch(console.error);
		}
	}

	// Data for children
	const [lBoxIdx, lSetBoxIdx] = useState<number>(0);

	const [lPalletIdx, lSetPalletIdx] = useState<number>(-1);

	const [lGripperName, lSetGripperName] = useState<string>('');
	const [lGripperTcp, lSetGripperTcp] = useState<number>(1);

	return (
		<CalibrationWizard
			pExit={pExit}
			pTarget={pTarget}
			pSteps={lSteps}
			pActiveStep={lActiveStep}
			pSetActiveStep={lSetActiveStep}
			pCompletedSteps={lCompletedSteps}
			pSetCompletedSteps={lSetCompletedSteps}
			pHasSavedCalibration={lHasSavedCalibration}
		>
			{
				<section className="full-size padding-left padding-right">
					{lActiveStep === lChooseBoxIdx && (
						<BoxStep
							save_item={save_item}
							pBoxIdx={lBoxIdx}
							pSetBoxIdx={lSetBoxIdx}
						/>
					)}
					{lActiveStep === lChoosePalletIdx && (
						<PalletStep
							save_item={save_item}
							pPalletIdx={lPalletIdx}
							pSetPalletIdx={lSetPalletIdx}
						/>
					)}
					{lActiveStep === lSelectGripperIdx && (
						<SelectGripperStep
							pGripperName={lGripperName}
							pSetGripperName={lSetGripperName}
							pGripperTcp={lGripperTcp}
							pSetGripperTcp={lSetGripperTcp}
							pSlipSheetPresent={pSlipSheetPresent}
							pCalibration={pPalletCalibration}
						/>
					)}
					{lActiveStep === lSetPositionIdx && (
						<SavePositionStep
							pTip={'Calibrate using the corner closest to the pick location.'}
							pCanSaveCalibration={can_save_calibration()}
							pShowSaveSuccess={lShowSaveSuccess}
							pSetShowSaveSuccess={lSetShowSaveSuccess}
							pHasSavedCalibration={lHasSavedCalibration}
							pHandleSavePosition={lHandleSavePosition}
							pCalibration={pPalletCalibration}
						/>
					)}
				</section>
			}
		</CalibrationWizard>
	);
}

interface ConveyorWizardProps {
	pExit: () => void;
	pTarget: string;
	pConveyorCalibration: ConveyorCalibration_t;
	pSaveCalibration: (
		c: ConveyorCalibration_t,
		submit: boolean,
		target: keyof Calibration_t
	) => void;
	pSlipSheetPresent: boolean;
}

function ConveyorWizard({
	pExit,
	pTarget,
	pConveyorCalibration,
	pSaveCalibration,
	pSlipSheetPresent,
}: ConveyorWizardProps) {
	// WIZARD MODEL:
	const lChooseBoxIdx = 0;
	const lBoxPositionIdx = 1;
	const lSelectGripperIdx = 2;
	const lSetPositionIdx = 3;
	const lSteps = [
		'Choose Box',
		'Box Pick Position',
		'Select Gripper',
		'Set Position',
	];
	const [lActiveStep, lSetActiveStep] = useState<number>(lChooseBoxIdx);
	const [lCompletedSteps, lSetCompletedSteps] = useState<boolean[]>([
		false,
		false,
		false,
	]);

	// SAVING STUFF:
	const save_item =
		(k: keyof ConveyorCalibration_t | keyof PalletCalibration_t) => (a: any) => {
			k = k as keyof ConveyorCalibration_t;
			const cp = { ...pConveyorCalibration };
			(cp as any)[k] = a;
			// save if position === "k"
			pSaveCalibration(cp, 'position' === k, pTarget as keyof Calibration_t);
		};

	// SAVE POSITION STUFF
	const app = useContext(ApplicationContext) as Application;

	const [lHasSavedCalibration, lSetHasSavedCalibration] =
		useState<boolean>(false);
	const [lShowSaveSuccess, lSetShowSaveSuccess] = useState<boolean>(false);

	const can_save_calibration = () =>
		lCompletedSteps.slice(0, lSetPositionIdx - 1).filter(c => !c).length === 0;

	const [lCalibrationBeforeSave, lSetCalibrationBeforeSave] =
		useState<ConveyorCalibration_t | null>(null);
	useEffect(() => {
		if (!lCalibrationBeforeSave) return;
		if (
			JSON.stringify(lCalibrationBeforeSave) !==
			JSON.stringify(pConveyorCalibration)
		) {
			const lCp = [...lCompletedSteps];
			lCp[lSetPositionIdx] = true;
			lSetCompletedSteps(lCp);
			lSetShowSaveSuccess(true);
			lSetHasSavedCalibration(true);
		}
	}, [pConveyorCalibration]);

	function lHandleSavePosition(pCalibrationBeforeSave: ConveyorCalibration_t) {
		lSetCalibrationBeforeSave(pCalibrationBeforeSave);
		if (app) {
			app
				.get_position(CALIB_TCP_INDEX.Box)
				.then(p => {
					if (!('posx' in (p as any))) {
						console.error('get_position', p);
					} else {
						save_item('position')((p as any)['posx']);
					}
					// const p_temp = new Array(6).fill(Math.random() * 1000);
					// set_position(p_temp);
				})
				.catch(console.error);
		}
	}

	// Data for children
	const [lBoxIdx, lSetBoxIdx] = useState<number>(0);
	const [lBoxPosition, lSetBoxPosition] = useState<number>(pConveyorCalibration.indexing_corner === 'Left' ? 0 : 1);
	const [lConveyorOrientation, lSetConveyorOrientation] =
		useState<ConveyorOrientation>(ConveyorOrientation.FromTop);

	// Need to read gripper value from mqtt.
	const [lGripperName, lSetGripperName] = useState<string>('');
	const [lGripperTcp, lSetGripperTcp] = useState<number>(1);

	return (
		<CalibrationWizard
			pExit={pExit}
			pTarget={pTarget}
			pSteps={lSteps}
			pActiveStep={lActiveStep}
			pSetActiveStep={lSetActiveStep}
			pCompletedSteps={lCompletedSteps}
			pSetCompletedSteps={lSetCompletedSteps}
			pHasSavedCalibration={lHasSavedCalibration}
		>
			{
				<section className="full-size padding-left padding-right">
					{lActiveStep === lChooseBoxIdx && (
						<BoxStep
							save_item={save_item}
							pBoxIdx={lBoxIdx}
							pSetBoxIdx={lSetBoxIdx}
						/>
					)}
					{lActiveStep === lBoxPositionIdx && (
						<BoxAlignStep
							save_item={save_item}
							pBoxPosition={lBoxPosition}
							pSetBoxPosition={lSetBoxPosition}
							pConveyorOrientation={lConveyorOrientation}
							pSetConveyorOrientation={lSetConveyorOrientation}
						/>
					)}
					{lActiveStep === lSelectGripperIdx && (
						<SelectGripperStep
							pGripperName={lGripperName}
							pSetGripperName={lSetGripperName}
							pGripperTcp={lGripperTcp}
							pSetGripperTcp={lSetGripperTcp}
							pSlipSheetPresent={pSlipSheetPresent}
							pCalibration={pConveyorCalibration}
						/>
					)}
					{lActiveStep === lSetPositionIdx && (
						<SavePositionStep
							pTip="Place the gripper so the suction cups are slightly pressed against
						the box."
							pCanSaveCalibration={can_save_calibration()}
							pShowSaveSuccess={lShowSaveSuccess}
							pSetShowSaveSuccess={lSetShowSaveSuccess}
							pHasSavedCalibration={lHasSavedCalibration}
							pHandleSavePosition={lHandleSavePosition}
							pCalibration={pConveyorCalibration}
						/>
					)}
				</section>
			}
		</CalibrationWizard>
	);
}

interface ListProps {
	save_calibration: any;
	savedCalibration: any;
	setSavedCalibration: any;
	pTargets: string[];
	hasUnsavedTune: boolean;
	setHasUnsavedTune: any;
	showConfirmLeaveModal: boolean;
	setShowConfirmLeaveModal: any;
}

function ConveyorList({
	save_calibration,
	savedCalibration,
	setSavedCalibration,
	pTargets,
	hasUnsavedTune,
	setHasUnsavedTune,
	showConfirmLeaveModal,
	setShowConfirmLeaveModal,
}: ListProps) {
	return (
		<CalibrationListDelegate
			pTitle={'Conveyor'}
			pType={CalibrationType.Conveyor}
			pTargets={pTargets}
			savedCalibration={savedCalibration}
			save_calibration={save_calibration}
			setSavedCalibration={setSavedCalibration}
			hasUnsavedTune={hasUnsavedTune}
			setHasUnsavedTune={setHasUnsavedTune}
			showConfirmLeaveModal={showConfirmLeaveModal}
			setShowConfirmLeaveModal={setShowConfirmLeaveModal}
		/>
	);
}

function PalletsList({
	save_calibration,
	savedCalibration,
	setSavedCalibration,
	pTargets,
	hasUnsavedTune,
	setHasUnsavedTune,
	showConfirmLeaveModal,
	setShowConfirmLeaveModal,
}: ListProps) {
	return (
		<CalibrationListDelegate
			pTitle="Pallet"
			pType={CalibrationType.Pallet}
			pTargets={pTargets}
			save_calibration={save_calibration}
			savedCalibration={savedCalibration}
			setSavedCalibration={setSavedCalibration}
			hasUnsavedTune={hasUnsavedTune}
			setHasUnsavedTune={setHasUnsavedTune}
			showConfirmLeaveModal={showConfirmLeaveModal}
			setShowConfirmLeaveModal={setShowConfirmLeaveModal}
		/>
	);
}

function SlipsheetPickList({
	save_calibration,
	savedCalibration,
	setSavedCalibration,
	pTargets,
	hasUnsavedTune,
	setHasUnsavedTune,
	showConfirmLeaveModal,
	setShowConfirmLeaveModal,
}: ListProps) {
	return (
		<CalibrationListDelegate
			pTitle="Slip Sheet Pick"
			pType={CalibrationType.Slipsheet}
			pTargets={pTargets}
			save_calibration={save_calibration}
			savedCalibration={savedCalibration}
			setSavedCalibration={setSavedCalibration}
			hasUnsavedTune={hasUnsavedTune}
			setHasUnsavedTune={setHasUnsavedTune}
			showConfirmLeaveModal={showConfirmLeaveModal}
			setShowConfirmLeaveModal={setShowConfirmLeaveModal}
		/>
	);
}

function SlipsheetPlaceList({
	save_calibration,
	savedCalibration,
	setSavedCalibration,
	pTargets,
	hasUnsavedTune,
	setHasUnsavedTune,
	showConfirmLeaveModal,
	setShowConfirmLeaveModal,
}: ListProps) {
	return (
		<CalibrationListDelegate
			pTitle="Slip Sheet Place"
			pType={CalibrationType.SlipsheetPlace}
			pTargets={pTargets}
			save_calibration={save_calibration}
			savedCalibration={savedCalibration}
			setSavedCalibration={setSavedCalibration}
			hasUnsavedTune={hasUnsavedTune}
			setHasUnsavedTune={setHasUnsavedTune}
			showConfirmLeaveModal={showConfirmLeaveModal}
			setShowConfirmLeaveModal={setShowConfirmLeaveModal}
		/>
	);
}

export interface CalibrationProps {
	pTab: TabOption;
}

export const to_calibration = (a: any): Calibration_t => {
	const c = default_Calibration_t();
	Object.keys(c).forEach(function (k) {
		if (k in a) {
			(c as any)[k] = a[k];
		}
	});
	return c;
};

export default function Calibration({ pTab }: CalibrationProps) {
	const app = useContext(ApplicationContext) as Application;
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const lStatus = lRuntimeContext.operational_state.status;

	const [hasUnsavedTune, setHasUnsavedTune] = useState<boolean>(false);
	const [showConfirmLeaveModal, setShowConfirmLeaveModal] =
		useState<boolean>(false);

	const [savedCalibration, setSavedCalibration] = useState<any>(null);
	const [pallet_config, set_pallet_config] =
		useState<PalletConfiguration | null>(null);
	// const [target, set_target] = useState<null | string>(null);

	const save_calibration =
		(for_field: keyof Calibration_t) =>
		(
			c: PalletCalibration_t | ConveyorCalibration_t | SlipSheetCalibration_t,
			submit: boolean,
			target: keyof Calibration_t
		) => {
			const cp = { ...savedCalibration };
			cp[for_field] = c;
			if (submit) {
				cp[for_field].date_modified = dateToString(new Date());
				cp[for_field].tune = [0, 0, 0, 0, 0, 0];
			}
			setSavedCalibration(cp);
			if (!submit) return;
			const boxes = pallet_config?.boxes || [];
			const pallets = pallet_config?.pallets || [];
			Object.keys(cp).forEach(function (k) {
				// copy pallet and box dimensions onto the configuraitons.
				// this is to avoid later reference errors.

				if (k === target) {
					switch (k) {
						case 'Conveyor 1':
						case 'Conveyor 2':
							const item_c = cp[k] as ConveyorCalibration_t;
							item_c.box_dimensions = boxes[item_c.box_index]?.dimensions || {
								width: 10000,
								length: 10000,
								height: 10000,
							};
							break;
						case 'Left Pallet':
						case 'Right Pallet':
							const item_p = cp[k] as PalletCalibration_t;
							item_p.box_dimensions = boxes[item_p.box_index]?.dimensions || {
								width: 10000,
								length: 10000,
								height: 10000,
							};
							item_p.pallet_dimensions = pallets[item_p.pallet_index]?.Dimensions || {
								width: 1,
								length: 1,
								height: 0,
							};
							cp[k].box_index = 0;
							break;
						case 'Slip Sheet Pick Left 1':
						case 'Slip Sheet Pick Left 2':
						case 'Slip Sheet Pick Right 1':
						case 'Slip Sheet Pick Right 2':
						case 'Slip Sheet Place Left':
						case 'Slip Sheet Place Right':
							break;
						default:
							console.error(`UNHANDLED ${k}`);
					}

					// Drop the box index before saving
				}
			});
			// save the calibration.
			app.api.Generic.set(['CALIBRATION'], cp)
				.then(function (result) {
					console.log('saved', result.status);
				})
				.catch(console.error);
		};

	useEffect(() => {
		if (!app?.api) return;
		app.api.Generic.get('CALIBRATION')
			.then(function (data) {
				const c = to_calibration(data) as Calibration_t;
				// Assign 0 as default box_index
				Object.keys(c).forEach(function (k: any) {
					const k_t = k as keyof Calibration_t;
					const cal = c[k_t];
					if ('box_index' in cal) cal.box_index = 0;
				});
				setSavedCalibration(c);
			})
			.catch(console.error);
		if (!isWebDeployment()) {
			app.api.Pallet.get()
				.then(pRes => {
					set_pallet_config(pRes.config);
				})
				.catch(console.error);
		}
	}, [app]);

	const is_disabled = (() => {
		switch (lStatus) {
			case PalletizerStatus.Ready:
			case PalletizerStatus.Homing:
				return false;
			case PalletizerStatus.Running:
			case PalletizerStatus.Fault:
			case PalletizerStatus.Offline:
				return true; // offline.
			default:
				return false;
		}
	})();

	const lConveyorsIdx = 0;
	const lPalletsIdx = 1;
	const lSlipSheetIdx = 2;
	const lSlipSheetPlaceIdx = 3;
	const lConfigurationIdx = 4;
	const [lCalibTypeIndex, lSetCalibTypeIndex] = useState<number>(lConveyorsIdx);
	const [lMachineConfig, lSetMachineConfig] = useState<
		MachineConfig | undefined
	>(void 0);

	function fetchMachineConfig() {
		app.api.Machine.get()
			.then(pMachineConfig => {
				lSetMachineConfig(pMachineConfig as MachineConfig);
			})
			.catch(console.error);
	}

	useEffect(() => {
		fetchMachineConfig();
	}, []);

	const [lConvTargets, lSetConvTargets] = useState<string[]>([]);
	const [lPalletTargets, lSetPalletTargets] = useState<string[]>([]);
	const [lSSPickTargets, lSetSSPickTargets] = useState<string[]>([]);
	const [lSSPlaceTargets, lSetSSPlaceTargets] = useState<string[]>([]);

	useEffect(() => {
		const pT = [];
		const SSPick = [];
		const SSPlace = [];
		const lBays = lMachineConfig?.Bays || [];
		for (const bay of lBays) {
			if (bay.Enabled) {
				pT.push(bay.Name + ' Pallet');
				const lMags = bay.SlipSheetMags;
				let lHasMag = false;
				for (let i = 0; i < lMags.length; i++) {
					if (lMags[i]) {
						lHasMag = true;
						SSPick.push('Slip Sheet Pick ' + bay.Name + ' ' + (i + 1));
					}
				}
				if (lHasMag) {
					SSPlace.push('Slip Sheet Place ' + bay.Name);
				}
			}
		}

		let lInfeeds = lMachineConfig?.Infeeds || [];
		const pattern = /Conveyor \d/;
		lInfeeds = lInfeeds.filter(feedme => feedme.match(pattern));

		lSetSSPickTargets(SSPick);
		lSetSSPlaceTargets(SSPlace);
		lSetConvTargets(lInfeeds);
		lSetPalletTargets(pT);
	}, [lMachineConfig]);

	const [locked, setLocked] = useState<boolean>(true);

	const lButtonClass =
		'full-width-minus-pad background-silver vh-9 flex-center align-center rounded margin-bottom margin-top color-blue';
	const lSelectedClass = ' bold-font blue-inner-border';
	return (
		<LayoutContent pTab={{ Name: '', IconClass: '' }} hideHeader={true}>
			<>
				<div className="full-size grid-col-18 vh-91">
					<div className="full-size flex-center">
						<div className="flex-col align-center border full-width-minus-pad full-height-minus-pad rounded">
							{[
								'Conveyors',
								'Pallets',
								'Slip Sheet Pick',
								'Slip Sheet Place',
								'Configuration',
							].map((pTitle, pIdx) => {
								const lActive = lCalibTypeIndex === pIdx;
								return (
									<div
										key={pIdx}
										onClick={() => {
											if (hasUnsavedTune) {
												setShowConfirmLeaveModal(true);
											} else {
												lSetCalibTypeIndex(pIdx);
											}
										}}
										className={lButtonClass + ` ${lActive ? lSelectedClass : ''}`}
									>
										<div className="flex-col align-center">
											<span className={'font-5 '}>{pTitle}</span>
										</div>
									</div>
								);
							})}
						</div>
					</div>
					{/* right pane */}
					<div className="full-size flex-center">
						<div className="full-height-minus-pad full-width-minus-pad border rounded flex-center ">
							<div className="full-height-minus-pad full-width-minus-pad flex-col align-center">
								{lCalibTypeIndex === lConveyorsIdx && (
									<ConveyorList
										save_calibration={save_calibration}
										savedCalibration={savedCalibration}
										setSavedCalibration={setSavedCalibration}
										pTargets={lConvTargets}
										hasUnsavedTune={hasUnsavedTune}
										setHasUnsavedTune={setHasUnsavedTune}
										showConfirmLeaveModal={showConfirmLeaveModal}
										setShowConfirmLeaveModal={setShowConfirmLeaveModal}
									/>
								)}
								{lCalibTypeIndex === lPalletsIdx && (
									<PalletsList
										save_calibration={save_calibration}
										savedCalibration={savedCalibration}
										setSavedCalibration={setSavedCalibration}
										pTargets={lPalletTargets}
										hasUnsavedTune={hasUnsavedTune}
										setHasUnsavedTune={setHasUnsavedTune}
										showConfirmLeaveModal={showConfirmLeaveModal}
										setShowConfirmLeaveModal={setShowConfirmLeaveModal}
									/>
								)}
								{lCalibTypeIndex === lSlipSheetIdx && (
									<SlipsheetPickList
										save_calibration={save_calibration}
										savedCalibration={savedCalibration}
										setSavedCalibration={setSavedCalibration}
										pTargets={lSSPickTargets}
										hasUnsavedTune={hasUnsavedTune}
										setHasUnsavedTune={setHasUnsavedTune}
										showConfirmLeaveModal={showConfirmLeaveModal}
										setShowConfirmLeaveModal={setShowConfirmLeaveModal}
									/>
								)}
								{lCalibTypeIndex === lSlipSheetPlaceIdx && (
									<SlipsheetPlaceList
										save_calibration={save_calibration}
										savedCalibration={savedCalibration}
										setSavedCalibration={setSavedCalibration}
										pTargets={lSSPlaceTargets}
										hasUnsavedTune={hasUnsavedTune}
										setHasUnsavedTune={setHasUnsavedTune}
										showConfirmLeaveModal={showConfirmLeaveModal}
										setShowConfirmLeaveModal={setShowConfirmLeaveModal}
									/>
								)}
								{lCalibTypeIndex === lConfigurationIdx && (
									<BayForm updateMachineConfig={fetchMachineConfig} />
								)}
							</div>
						</div>
					</div>
					{/* {locked && (
					<Unlock
						close={() => {
							setLocked(false);
						}}
					></Unlock>
				)} */}
				</div>
				{locked && (
					<Unlock
						close={() => {
							setLocked(false);
						}}
					></Unlock>
				)}
			</>
		</LayoutContent>
	);
}
